import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, formatApiError } from '../auth/AuthContext';
import { useT } from '../i18n/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function LoginPage() {
  const { login } = useAuth();
  const { lang } = useT();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const S = {
    de: { title: 'Zugang zur Präsentation', sub: 'Melde dich mit deinen Presenter-Zugangsdaten an', email: 'E-Mail', password: 'Passwort', submit: 'Anmelden', submitting: 'Wird angemeldet…', overline: 'Vertraulich · Nur für Presenter' },
    en: { title: 'Presentation Access', sub: 'Sign in with your presenter credentials', email: 'Email', password: 'Password', submit: 'Sign in', submitting: 'Signing in…', overline: 'Confidential · Presenters only' },
    es: { title: 'Acceso a la presentación', sub: 'Inicia sesión con tus credenciales de presenter', email: 'Correo', password: 'Contraseña', submit: 'Iniciar sesión', submitting: 'Iniciando sesión…', overline: 'Confidencial · Sólo presenters' },
    it: { title: 'Accesso alla presentazione', sub: 'Accedi con le credenziali del presenter', email: 'Email', password: 'Password', submit: 'Accedi', submitting: 'Accesso in corso…', overline: 'Riservato · Solo presenter' },
    pt: { title: 'Acesso à apresentação', sub: 'Entre com as suas credenciais de presenter', email: 'E-mail', password: 'Palavra-passe', submit: 'Entrar', submitting: 'A entrar…', overline: 'Confidencial · Apenas presenters' },
    pl: { title: 'Dostęp do prezentacji', sub: 'Zaloguj się swoimi danymi presentera', email: 'E-mail', password: 'Hasło', submit: 'Zaloguj się', submitting: 'Logowanie…', overline: 'Poufne · Tylko dla presenterów' },
    cs: { title: 'Přístup k prezentaci', sub: 'Přihlaste se svými přihlašovacími údaji presentera', email: 'E-mail', password: 'Heslo', submit: 'Přihlásit se', submitting: 'Přihlašuji…', overline: 'Důvěrné · Pouze pro presentery' },
    sk: { title: 'Prístup k prezentácii', sub: 'Prihláste sa svojimi presenter údajmi', email: 'E-mail', password: 'Heslo', submit: 'Prihlásiť sa', submitting: 'Prihlasujem…', overline: 'Dôverné · Iba pre presenterov' },
    hu: { title: 'Prezentáció elérése', sub: 'Jelentkezz be a presenter belépési adataiddal', email: 'E-mail', password: 'Jelszó', submit: 'Belépés', submitting: 'Bejelentkezés…', overline: 'Bizalmas · Csak presenterek' },
    ro: { title: 'Acces la prezentare', sub: 'Autentifică-te cu datele tale de presenter', email: 'E-mail', password: 'Parolă', submit: 'Autentificare', submitting: 'Autentificare…', overline: 'Confidenţial · Doar pentru presenteri' },
    sr: { title: 'Pristup prezentaciji', sub: 'Prijavi se svojim presenter kredencijalima', email: 'E-mail', password: 'Lozinka', submit: 'Prijava', submitting: 'Prijavljivanje…', overline: 'Poverljivo · Samo za presentere' },
    tr: { title: 'Sunumua erişim', sub: 'Presenter kimlik bilgilerinle giriş yap', email: 'E-posta', password: 'Şifre', submit: 'Giriş yap', submitting: 'Giriş yapılıyor…', overline: 'Gizli · Sadece Presenterlar için' },
  };
  const strings = S[lang] || S.en;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await login(email.trim(), password);
      nav('/dashboard', { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-shell" data-testid="login-page">
      <div className="login-bg-grain" aria-hidden />
      <div className="login-bg-radial" aria-hidden />

      <div className="login-lang-wrap" data-testid="login-lang-toggle">
        <LanguageSwitcher variant="ghost" />
      </div>

      <div className="login-brandmark">
        <span className="login-brandmark-icon">◆</span>
        <span className="login-brandmark-text">STIG&nbsp;GROUP</span>
      </div>

      <form onSubmit={onSubmit} className="login-card" data-testid="login-form">
        <div className="login-overline">{strings.overline}</div>
        <h1 className="login-title font-display">{strings.title}</h1>
        <p className="login-sub">{strings.sub}</p>

        <label className="login-label" htmlFor="login-email">{strings.email}</label>
        <input
          id="login-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="login-input"
          autoComplete="username"
          required
          data-testid="login-email-input"
        />

        <label className="login-label" htmlFor="login-password">{strings.password}</label>
        <input
          id="login-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="login-input"
          autoComplete="current-password"
          required
          data-testid="login-password-input"
        />

        {error && <div className="login-error" data-testid="login-error">{error}</div>}

        <button
          type="submit"
          disabled={busy}
          className="login-submit"
          data-testid="login-submit-button"
        >
          <span>{busy ? strings.submitting : strings.submit}</span>
        </button>
      </form>

      <div className="login-footer">© {new Date().getFullYear()} Stig Group · All rights reserved</div>
    </div>
  );
}
